import NiceModal from '@ebay/nice-modal-react'
import { signInWithRedirect } from 'aws-amplify/auth'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { constants, STATUS_LOGIN } from '@/lib/constants'
import { ROUTES } from '@/lib/routes'
import { getWithExpiry, setWithExpiry, storageAvailable } from '@/lib/utilities/system'
import { addToDataLayer } from '@/lib/utilities/vehicle'
import { postAnalyticsEvent } from '@/store/actions/event'
import { storeRedirectUrl, storeUsedSocialLogin } from '@/store/reducers/user'
import { selectIsAuthenticated } from '@/store/selectors/user'
import { Button, ButtonHierarchy } from '../Button'
import styles from './one-tap.module.scss'
import { Size } from '@/types/system'
import { useAdminMode } from '@/lib/hooks/useAdminMode'
import { useCookieBot } from '@/lib/hooks/useCookiebot'

const Icon = dynamic(() => import('@/components/styleguide/Icon'))
const AuthenticationModal = dynamic(() => import('@/components/modals/AuthenticationModal'), {
  ssr: false,
})

export const OneTap = () => {
  const dispatch = useDispatch()
  const router = useRouter()

  const isAdminMode = useAdminMode()
  const cookiebot = useCookieBot()
  const isAuthenticated = useSelector(selectIsAuthenticated)

  const [isVisible, setIsVisible] = useState(false)
  const [showMore, setShowMore] = useState(false)

  useEffect(() => {
    const routerAsPathNoPrams = router.asPath.split('?')[0]
    const isOnLandingPage = [
      ROUTES.enquiryLandingPage.regex,
      ROUTES.enquiryLandingAltPage.regex,
    ]
      .some(regex => regex.test(routerAsPathNoPrams))
    let storageKeyExists = false

    if (storageAvailable('localStorage')) {
      const storageKey = getWithExpiry(constants.localStorageKey.oneTap)
      storageKeyExists = !!storageKey
    }

    const shouldHideOneTap = isOnLandingPage || storageKeyExists || cookiebot.isVisible

    if (router.isReady) {
      if (shouldHideOneTap && isVisible) {
        setIsVisible(false)
      }
      if (!shouldHideOneTap && !isVisible) {
        const showTimer = setTimeout(() => {
          setIsVisible(true)
          addToDataLayer({
            event: 'one-tap-view',
          })
          dispatch(
            postAnalyticsEvent({
              event: constants.eventTypes.oneTapComponentView,
            }),
          )
        }, 1500)
        return () => {
          clearTimeout(showTimer)
        }
      }
    }
  }, [dispatch, router, isVisible, cookiebot.isVisible])

  const onCloseOneTap = () => {
    if (storageAvailable('localStorage')) {
      const oneDay = 86400000
      setWithExpiry(constants.localStorageKey.oneTap, true, oneDay)
    }
    addToDataLayer({
      event: `social-one-tap`,
      label: 'close',
    })
    dispatch(
      postAnalyticsEvent({
        event: constants.eventTypes.oneTapComponentAction,
        payload: {
          label: 'close',
        },
      }),
    )
    setIsVisible(false)
  }

  const onLogIn = (platform: string) => {
    if (platform === 'facebook') {
      addToDataLayer({
        event: `social-one-tap`,
        label: 'facebook',
      })
      dispatch(
        postAnalyticsEvent({
          event: constants.eventTypes.oneTapComponentAction,
          payload: {
            label: 'facebook',
          },
        }),
      )
      dispatch(storeRedirectUrl(router.asPath))
      dispatch(storeUsedSocialLogin(true))
      signInWithRedirect({ provider: 'Facebook' })
    }

    if (platform === 'google') {
      addToDataLayer({
        event: `social-one-tap`,
        label: 'google',
      })
      dispatch(
        postAnalyticsEvent({
          event: constants.eventTypes.oneTapComponentAction,
          payload: {
            label: 'google',
          },
        }),
      )
      dispatch(storeRedirectUrl(router.asPath))
      dispatch(storeUsedSocialLogin(true))
      signInWithRedirect({ provider: 'Google' })
    }

    if (platform === 'email') {
      addToDataLayer({
        event: `social-one-tap`,
        label: 'email',
      })
      dispatch(
        postAnalyticsEvent({
          event: constants.eventTypes.oneTapComponentAction,
          payload: {
            label: 'email',
          },
        }),
      )
      NiceModal.show(AuthenticationModal as FC, {
        initialStatus: STATUS_LOGIN,
      })
    }
  }

  if (isAuthenticated || !isVisible || isAdminMode) {
    return null
  }

  return (
    <div className={`${styles.container} one-tap-component`}>
      <div className={styles.header}>
        <div className={styles['top-line']}>
          <Icon
            name="KeyRound"
            size={Size.Small}
          />{' '}
          <p>Unlock the best of LeaseLoco</p>
        </div>
        <div className={styles['bottom-line']}>
          <p>Log in to get the most out of LeaseLoco</p>
        </div>
      </div>
      <div className={styles['login-wrapper']}>
        <div className={styles.message}>
          <div className={styles.text}>
            <Image
              src={`${constants.imageHost.assets}/logos/icon.png`}
              alt=""
              height={20}
              width={20}
            />
            <p>Log in to LeaseLoco with Google</p>
          </div>
          <Button
            hierarchy={ButtonHierarchy.BorderlessGray}
            size={Size.Medium}
            label=""
            icon="X"
            onClick={onCloseOneTap}
          />
        </div>
        <div className={styles['button-group']}>
          <button
            type="button"
            className={styles.button}
            data-type="google"
            onClick={() => onLogIn('google')}>
            <div className={styles.image}>
              <Image
                src={`${constants.imageHost.assets}/general/google.svg`}
                alt=""
                height={18}
                width={18}
              />
            </div>
            <div className={styles.label}>
              <p>Continue with Google</p>
              <p>Link your Google account</p>
            </div>
            <span className={styles['google-button']}>Continue</span>
          </button>
          {showMore ? (
            <>
              <button
                type="button"
                className={styles.button}
                data-type="facebook"
                onClick={() => onLogIn('facebook')}>
                <div className={styles.image}>
                  <Image
                    src={`${constants.imageHost.assets}/icons/social/facebook.svg`}
                    alt=""
                    height={18}
                    width={18}
                  />
                </div>
                <div className={styles.label}>
                  <p>Continue with Facebook</p>
                  <p>Link your Facebook account</p>
                </div>
                <span className={styles['google-button']}>Continue</span>
              </button>
              <button
                type="button"
                className={styles.button}
                data-type="email"
                onClick={() => onLogIn('email')}>
                <div className={styles.image}>
                  <Icon
                    name="Mail"
                    size={Size.Small}
                  />
                </div>
                <div className={styles.label}>
                  <p>Continue with Email</p>
                </div>
                <span className={styles['google-button']}>Continue</span>
              </button>
            </>
          ) : (
            <button
              type="button"
              className={styles.button}
              data-type="show-more"
              onClick={() => setShowMore(true)}>
              2 more options{' '}
              <Icon
                name="ChevronDown"
                size={Size.Medium}
              />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
